import { useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { cn } from '@util'
import { isEmpty, isNotEmpty, type PermissionEntityId, type PermissionType } from '@terros/common'
import { Box, Text } from '@mantine/core'
import { ConnectionLayer, type Edge, type Position } from '../../connectionLayer'
import { type PermGraph, type PermGraphEdge, type PermGraphEdgeRelation, type PermGraphNode } from './types'
import { isPermGraphWinningEdge, type PermGraphWinningPath } from './permGraphWinningPath'
import { getPermGraphConnectorTheme } from './permGraphVisualState'
import { PermGraphNodeCard } from './PermGraphNodeCard'
import {
  getPermGraphLayoutMetrics,
  getPermGraphPathLayout,
  type PermGraphPathLayout,
  type PermGraphRoleSource,
} from './permGraphLayout'

type Props = {
  currentUserId: PermissionEntityId | undefined
  graph: PermGraph
  openId?: PermissionEntityId
  permissionType: PermissionType
  targetId: PermissionEntityId
  winningPath: PermGraphWinningPath
  renderEditor: (node: PermGraphNode) => ReactNode
  onToggle: (node: PermGraphNode) => void
}

const DENSE_ASSIGNMENT_EDGE_THRESHOLD = 8

const isInheritsRelation = (relation: PermGraphEdgeRelation): boolean => relation === 'inherits'

const isAssignmentRelation = (relation: PermGraphEdgeRelation): boolean => relation !== 'inherits'

type GraphNodePosition = {
  column: number
  level: number
}

type GraphNodeRenderer = (node: PermGraphNode) => ReactElement

export function PermGraphCanvas({
  currentUserId,
  graph,
  openId,
  permissionType,
  targetId,
  winningPath,
  renderEditor,
  onToggle,
}: Props): ReactElement {
  const [hoveredId, setHoveredId] = useState<PermissionEntityId>()
  const pathLayout = useMemo(() => getPermGraphPathLayout(graph), [graph])
  const layout = useMemo(() => {
    const levelNodeCounts = [
      pathLayout.defaultNodes.length,
      ...pathLayout.backboneLevels.map((nodes) =>
        nodes.reduce((count, node) => count + 1 + (pathLayout.roleSourcesByTarget.get(node.entityId)?.length ?? 0), 0)
      ),
    ]
    return getPermGraphLayoutMetrics(levelNodeCounts)
  }, [pathLayout])
  const nodePositions = useMemo(() => getPermGraphNodePositions(pathLayout), [pathLayout])
  const hasDenseAssignments = useMemo(
    () => graph.edges.filter((edge) => edge.relation !== 'inherits').length >= DENSE_ASSIGNMENT_EDGE_THRESHOLD,
    [graph]
  )
  const focusedId = openId ?? hoveredId
  const assignmentFocusedId = getAssignmentFocusedId(graph, focusedId)
  const inheritanceEdges = useMemo(
    () =>
      toConnectionEdges({
        graph,
        include: isInheritsRelation,
        nodePositions,
        permissionType,
        targetId,
        winningPath,
      }),
    [graph, nodePositions, permissionType, targetId, winningPath]
  )
  const assignmentEdges = useMemo(
    () =>
      toConnectionEdges({
        focusedId: assignmentFocusedId,
        graph,
        include: isAssignmentRelation,
        nodePositions,
        permissionType,
        subdueByDefault: hasDenseAssignments,
        targetId,
        winningPath,
      }),
    [assignmentFocusedId, graph, hasDenseAssignments, nodePositions, permissionType, targetId, winningPath]
  )
  const renderNode: GraphNodeRenderer = (node) => (
    <PermGraphNodeCard
      currentUserId={currentUserId}
      domId={toPermGraphDomId(targetId, permissionType, node.entityId)}
      editor={renderEditor(node)}
      isOpen={openId === node.entityId}
      key={node.entityId}
      node={node}
      width={layout.nodeWidth}
      onHoverChange={(isHovered) => {
        setHoveredId((currentId) => {
          if (isHovered) return node.entityId
          return currentId === node.entityId ? undefined : currentId
        })
      }}
      onToggle={() => onToggle(node)}
    />
  )

  return (
    <Box className='relative flex min-h-full min-w-max flex-col' data-density={layout.density} px='sm' py='lg'>
      <ConnectionLayer edges={inheritanceEdges} options={{ lineShape: 'square' }} />
      <ConnectionLayer edges={assignmentEdges} options={{ lineShape: 'square', lineType: 'dotted' }} />
      {hasDenseAssignments && (
        <Text c='dimmed' pb='sm' size='xs' ta='center'>
          Hover or open a node to highlight its role and membership connections.
        </Text>
      )}
      <div className={cn('flex flex-col', layout.rowGapClass)}>
        {isNotEmpty(pathLayout.defaultNodes) && (
          <div className={cn('flex flex-nowrap items-center justify-center', layout.columnGapClass)}>
            {pathLayout.defaultNodes.map(renderNode)}
          </div>
        )}
        {pathLayout.backboneLevels.map((nodes) => (
          <div
            className={cn('flex flex-nowrap items-center justify-center', layout.columnGapClass)}
            key={nodes.map((node) => node.entityId).join('|')}
          >
            {nodes.map((node) => (
              <PermGraphTargetCluster
                columnGapClass={layout.columnGapClass}
                key={node.entityId}
                node={node}
                nodeWidth={layout.nodeWidth}
                renderNode={renderNode}
                roleSources={pathLayout.roleSourcesByTarget.get(node.entityId) ?? []}
              />
            ))}
          </div>
        ))}
      </div>
    </Box>
  )
}

function PermGraphTargetCluster({
  columnGapClass,
  node,
  nodeWidth,
  renderNode,
  roleSources,
}: {
  columnGapClass: string
  node: PermGraphNode
  nodeWidth: number
  renderNode: GraphNodeRenderer
  roleSources: PermGraphRoleSource[]
}): ReactElement {
  if (isEmpty(roleSources)) return renderNode(node)

  const leftSources = roleSources.filter((source) => source.side === 'left')
  const rightSources = roleSources.filter((source) => source.side === 'right')

  return (
    <div className={cn('flex flex-nowrap items-center', columnGapClass)}>
      <div className='gap-2xl flex flex-col' style={{ width: nodeWidth }}>
        {leftSources.map((source) => renderNode(source.node))}
      </div>
      {renderNode(node)}
      <div className='gap-2xl flex flex-col' style={{ width: nodeWidth }}>
        {rightSources.map((source) => renderNode(source.node))}
      </div>
    </div>
  )
}

export function getAssignmentFocusedId(
  graph: PermGraph,
  focusedId: PermissionEntityId | undefined
): PermissionEntityId | undefined {
  const hasFocusedAssignment = graph.edges.some(
    (edge) => edge.relation !== 'inherits' && (edge.source === focusedId || edge.target === focusedId)
  )
  return hasFocusedAssignment ? focusedId : undefined
}

function toConnectionEdges({
  focusedId,
  graph,
  include,
  nodePositions,
  permissionType,
  subdueByDefault = false,
  targetId,
  winningPath,
}: {
  focusedId?: PermissionEntityId
  graph: PermGraph
  include: (relation: PermGraphEdgeRelation) => boolean
  nodePositions: Map<PermissionEntityId, GraphNodePosition>
  permissionType: PermissionType
  subdueByDefault?: boolean
  targetId: PermissionEntityId
  winningPath: PermGraphWinningPath
}): Edge[] {
  return graph.edges
    .filter((edge) => include(edge.relation))
    .map((edge) => {
      const sourceNode = graph.nodes.find((node) => node.entityId === edge.source)
      const connector = sourceNode
        ? getPermGraphConnectorTheme(sourceNode, isPermGraphWinningEdge(edge, winningPath))
        : undefined
      const isFocused = edge.source === focusedId || edge.target === focusedId
      const sourcePosition = nodePositions.get(edge.source)
      const targetPosition = nodePositions.get(edge.target)
      const shouldSubdue = focusedId ? !isFocused : subdueByDefault
      const anchors = toEdgeAnchors({ edge, sourcePosition, targetPosition })
      return {
        color: connector?.color,
        start: toPermGraphDomId(targetId, permissionType, edge.source),
        end: toPermGraphDomId(targetId, permissionType, edge.target),
        startPosition: anchors.startPosition,
        endPosition: anchors.endPosition,
        opacity: toEdgeOpacity({ focusedId, isFocused, subdueByDefault }),
        width: shouldSubdue ? 1 : connector?.width,
      }
    })
}

function toEdgeAnchors({
  edge,
  sourcePosition,
  targetPosition,
}: {
  edge: PermGraphEdge
  sourcePosition: GraphNodePosition | undefined
  targetPosition: GraphNodePosition | undefined
}): { endPosition?: Position; startPosition?: Position } {
  if (sourcePosition === undefined || targetPosition === undefined) return {}

  const sourceIsLeft = sourcePosition.column < targetPosition.column
  if (sourcePosition.level === targetPosition.level) {
    return { endPosition: sourceIsLeft ? 'left' : 'right', startPosition: sourceIsLeft ? 'right' : 'left' }
  }
  if (edge.relation === 'inherits' || sourcePosition.column === targetPosition.column) return {}

  return { endPosition: sourceIsLeft ? 'left' : 'right', startPosition: 'bottom' }
}

function toEdgeOpacity({
  focusedId,
  isFocused,
  subdueByDefault,
}: {
  focusedId: PermissionEntityId | undefined
  isFocused: boolean
  subdueByDefault: boolean
}): number | undefined {
  if (focusedId) return isFocused ? 1 : 0.08
  return subdueByDefault ? 0.16 : undefined
}

function getPermGraphNodePositions(pathLayout: PermGraphPathLayout): Map<PermissionEntityId, GraphNodePosition> {
  const positions = new Map<PermissionEntityId, GraphNodePosition>()
  pathLayout.defaultNodes.forEach((node, column) => positions.set(node.entityId, { column, level: 0 }))
  pathLayout.backboneLevels.forEach((nodes, level) => {
    nodes.forEach((node, clusterIndex) => {
      const targetColumn = clusterIndex * 3 + 1
      positions.set(node.entityId, { column: targetColumn, level: level + 1 })
      const roleSources = pathLayout.roleSourcesByTarget.get(node.entityId) ?? []
      roleSources.forEach((source) => {
        positions.set(source.node.entityId, {
          column: targetColumn + (source.side === 'left' ? -1 : 1),
          level: level + 1,
        })
      })
    })
  })
  return positions
}

export function toPermGraphDomId(
  targetId: PermissionEntityId,
  permissionType: PermissionType,
  entityId: PermissionEntityId
): string {
  return `permission-graph-${targetId}-${permissionType}-${entityId}`.replaceAll(':', '-')
}
